import type { ReactNode } from "react"
import { useMemo } from "react"

import { computeSpacerHeight } from "./beat-calculator"
import { useScrollProgress } from "./use-scroll-progress"

interface ScrollCaptureContainerProps {
  commentaryCounts: number[]
  children: (progress: number) => ReactNode
}

export function ScrollCaptureContainer({
  commentaryCounts,
  children,
}: ScrollCaptureContainerProps) {
  const { ref, progress } = useScrollProgress()

  const totalPlays = commentaryCounts.length
  const spacerHeight = useMemo(
    () => computeSpacerHeight(commentaryCounts),
    [commentaryCounts],
  )

  return (
    <section ref={ref}>
      <div style={{ height: spacerHeight }}>
        <div
          className="relative flex items-center justify-center"
          style={{
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "hidden",
            background:
              "radial-gradient(ellipse at center, oklch(0.14 0.02 55) 0%, oklch(0.08 0.01 55) 100%)",
          }}
        >
          {/* Cinematic vignette */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              boxShadow:
                "inset 0 0 120px 40px oklch(0 0 0 / 0.7), inset 0 0 60px 20px oklch(0 0 0 / 0.3)",
              zIndex: 10,
            }}
          />

          {/* CRT scanlines overlay */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, transparent, transparent 3px, oklch(0 0 0 / 0.04) 3px, oklch(0 0 0 / 0.04) 6px)",
              zIndex: 11,
            }}
          />

          {/* Subtle gold accent glow at top */}
          <div
            className="pointer-events-none absolute top-0 right-0 left-0 h-1"
            style={{
              background:
                "linear-gradient(90deg, transparent, oklch(0.78 0.18 55 / 0.4), transparent)",
              boxShadow: "0 0 30px 10px oklch(0.78 0.18 55 / 0.15)",
            }}
          />

          {/* Play counter — bottom right */}
          <div
            className="pointer-events-none absolute right-6 bottom-6 text-right"
            style={{ zIndex: 12 }}
          >
            <div
              className="font-display text-[10px] font-bold tracking-[0.3em] uppercase"
              style={{ color: "oklch(0.45 0.06 55)" }}
            >
              Key Play
            </div>
            <div
              className="font-display text-4xl font-black tabular-nums"
              style={{
                color: "oklch(0.55 0.10 55)",
                textShadow: "0 0 20px oklch(0.78 0.18 55 / 0.2)",
              }}
            >
              {Math.min(
                totalPlays,
                Math.max(1, Math.ceil(progress * totalPlays)),
              )}
              <span
                className="text-lg font-bold"
                style={{ color: "oklch(0.35 0.04 55)" }}
              >
                /{totalPlays}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="relative h-full w-full" style={{ zIndex: 5 }}>
            {children(progress)}
          </div>
        </div>
      </div>
    </section>
  )
}
